import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberTypeTable from '../../../components/tables/MemberTypeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberTypeSearch, useDeleteMemberType } from '../../../hooks/useMemberTypeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { MemberTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const MemberTypeList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<MemberTypeDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Direct API search (no caching)
  const { search } = useMemberTypeSearch();
  const deleteMemberType = useDeleteMemberType();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Member Types', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  // Fetch items from server
  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching MemberTypes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch when page/pageSize/searchFilters change (NOT formFilters)
  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters); // Apply form values to actual search
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: MemberTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteMemberType.mutateAsync(dto.id || '');
      // Refetch after delete
      await fetchItems();
    } catch (error) {
      console.error('Error deleting MemberType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/membertypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.MemberType.View"
    >
      <Card
        title="Manage Member Types"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/membertypes/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={formFilters.title || ''}
              onChange={(e) =>
                setFormFilters({ ...formFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <MemberTypeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/membertypes/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default MemberTypeList;
